import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { C } from '../tokens';
import Icon from '../components/Icon';
import { Card, Pill, Btn, Breadcrumb } from '../components/UI';
import useBreakpoint from '../useBreakpoint';

/* ═══════════════════════════════════════════
   Constants & Helpers
   ═══════════════════════════════════════════ */
const HOUR_H = 60;
const START_HOUR = 7;
const END_HOUR = 20;
const TOTAL_HOURS = END_HOUR - START_HOUR;
const SNAP_MIN = 15;
const SNAP_PX = (SNAP_MIN / 60) * HOUR_H;
const DRAG_THRESHOLD = 5;
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const COLORS = [C.teal, C.indigo, C.green, C.primary, C.amber, '#a855f7', '#3b82f6'];

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function getMonday(d) {
  const dt = new Date(d);
  const day = dt.getDay();
  dt.setDate(dt.getDate() + (day === 0 ? -6 : 1 - day));
  dt.setHours(0, 0, 0, 0);
  return dt;
}
function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function setHM(d, h, m) { const r = new Date(d); r.setHours(h, m, 0, 0); return r; }
function fmt(d) { return `${d.getDate()} ${MONTH_NAMES[d.getMonth()].slice(0, 3)}`; }
function fmtTime(h, m) { return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`; }
function fmtTimeFromDate(d) { return fmtTime(d.getHours(), d.getMinutes()); }

/** Snap a px offset to the nearest SNAP_MIN interval and return { hours, minutes } */
function snapY(y) {
  const snapped = Math.round(y / SNAP_PX) * SNAP_PX;
  const totalMin = Math.round((snapped / HOUR_H) * 60);
  const h = START_HOUR + Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return { h: Math.max(START_HOUR, Math.min(END_HOUR - 1, h)), m: Math.max(0, Math.min(59, m)) };
}

/* ═══════════════════════════════════════════
   Seed events
   ═══════════════════════════════════════════ */
function seedEvents() {
  const mon = getMonday(new Date());
  return [
    { id: 1, title: 'Product Design Sync', start: setHM(mon, 9, 0), end: setHM(mon, 11, 0), color: C.teal, type: 'Online', location: 'Zoom' },
    { id: 2, title: 'Stakeholder Review', start: setHM(addDays(mon, 1), 12, 0), end: setHM(addDays(mon, 1), 13, 30), color: C.green, type: 'On-site', location: 'HQ Room 3' },
    { id: 3, title: 'Sprint Planning', start: setHM(addDays(mon, 2), 10, 0), end: setHM(addDays(mon, 2), 12, 30), color: C.indigo, type: 'Online', location: 'Google Meet' },
    { id: 4, title: 'Client Call: Vertex', start: setHM(addDays(mon, 3), 15, 0), end: setHM(addDays(mon, 3), 16, 0), color: C.primary, type: 'Online', location: 'Zoom' },
    { id: 5, title: 'Team Standup', start: setHM(addDays(mon, 4), 9, 0), end: setHM(addDays(mon, 4), 9, 30), color: C.amber, type: 'Internal', location: 'Slack Huddle' },
    { id: 6, title: 'AI Model Review', start: setHM(addDays(mon, 2), 14, 0), end: setHM(addDays(mon, 2), 15, 30), color: '#a855f7', type: 'Online', location: 'Meet' },
    { id: 7, title: '1:1 with Sarah', start: setHM(mon, 14, 0), end: setHM(mon, 14, 45), color: '#3b82f6', type: 'Internal', location: 'Office' },
    { id: 8, title: 'Quarterly Planning', start: setHM(addDays(mon, 3), 10, 0), end: setHM(addDays(mon, 3), 12, 0), color: C.teal, type: 'On-site', location: 'Board Room' },
    { id: 9, title: 'Design Critique', start: setHM(addDays(mon, 1), 9, 0), end: setHM(addDays(mon, 1), 10, 0), color: C.amber, type: 'Online', location: 'Figma' },
    { id: 10, title: 'Lunch with Investors', start: setHM(addDays(mon, 4), 12, 0), end: setHM(addDays(mon, 4), 13, 30), color: C.green, type: 'On-site', location: 'Restaurant' },
  ];
}

/* ═══════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════ */
export default function SchedulingPage() {
  const { isMobile, isTablet, isDesktop, isWide } = useBreakpoint();
  const mob = isMobile;
  const pad = mob ? 16 : isTablet ? 24 : 36;
  const canDrag = isDesktop; /* drag only on ≥992px */
  const timeColW = mob ? 44 : 56;

  const today = useMemo(() => new Date(), []);
  const [weekStart, setWeekStart] = useState(() => getMonday(today));
  /* Mobile defaults to day view */
  const [view, setView] = useState(() => isMobile ? 'day' : 'week');
  const [events, setEvents] = useState(seedEvents);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [newEvent, setNewEvent] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [filter, setFilter] = useState('All');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [dayIndex, setDayIndex] = useState(() => { const d = today.getDay(); return d === 0 ? 6 : d - 1; });
  const nextId = useRef(11);

  /* ─── Drag state (desktop only) ─── */
  const dragRef = useRef(null);
  const ghostRef = useRef(null);
  const [dragGhost, setDragGhost] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const columnRectsRef = useRef([]);

  /** Update ghost state AND ref together */
  const updateGhost = useCallback((val) => {
    ghostRef.current = val;
    setDragGhost(val);
  }, []);

  // Live clock
  useEffect(() => {
    const iv = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(iv);
  }, []);

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  /* ─── Navigation ─── */
  const goPrev = () => {
    if (view === 'week') setWeekStart(addDays(weekStart, -7));
    else if (view === 'day') {
      setDayIndex(p => p === 0 ? 6 : p - 1);
      if (addDays(weekDays[dayIndex], -1) < weekStart) setWeekStart(addDays(weekStart, -7));
    } else {
      setWeekStart(prev => { const d = new Date(prev); d.setMonth(d.getMonth() - 1); return getMonday(d); });
    }
  };
  const goNext = () => {
    if (view === 'week') setWeekStart(addDays(weekStart, 7));
    else if (view === 'day') {
      setDayIndex(p => p === 6 ? 0 : p + 1);
      if (addDays(weekDays[dayIndex], 1) >= addDays(weekStart, 7)) setWeekStart(addDays(weekStart, 7));
    } else {
      setWeekStart(prev => { const d = new Date(prev); d.setMonth(d.getMonth() + 1); return getMonday(d); });
    }
  };
  const goToday = () => { setWeekStart(getMonday(today)); const d = today.getDay(); setDayIndex(d === 0 ? 6 : d - 1); };

  /* ─── Derived data ─── */
  const filteredEvents = useMemo(() => filter === 'All' ? events : events.filter(e => e.type === filter), [events, filter]);
  const eventsForDay = useCallback((date) => filteredEvents.filter(e => sameDay(e.start, date)), [filteredEvents]);

  // Title string
  const titleStr = useMemo(() => {
    if (view === 'day') {
      const d = weekDays[dayIndex];
      return mob
        ? `${DAY_NAMES[d.getDay()]}, ${d.getDate()} ${MONTH_NAMES[d.getMonth()].slice(0, 3)}`
        : `${DAY_NAMES[d.getDay()]}, ${d.getDate()} ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
    }
    if (view === 'month') return `${MONTH_NAMES[weekStart.getMonth()]} ${weekStart.getFullYear()}`;
    const end = addDays(weekStart, 6);
    if (mob) return `${fmt(weekStart)} – ${fmt(end)}`;
    return weekStart.getMonth() === end.getMonth()
      ? `${fmt(weekStart)} – ${end.getDate()} ${MONTH_NAMES[end.getMonth()]} ${end.getFullYear()}`
      : `${fmt(weekStart)} – ${fmt(end)} ${end.getFullYear()}`;
  }, [view, weekStart, weekDays, dayIndex, mob]);

  const nowTop = useMemo(() => {
    const h = currentTime.getHours(), m = currentTime.getMinutes();
    return (h < START_HOUR || h >= END_HOUR) ? null : (h - START_HOUR) * HOUR_H + (m / 60) * HOUR_H;
  }, [currentTime]);

  const upcomingToday = useMemo(() =>
    events.filter(ev => sameDay(ev.start, today) && ev.end >= currentTime).sort((a, b) => a.start - b.start).slice(0, 4),
    [events, today, currentTime]
  );

  const weekEventCount = useMemo(() => {
    const end = addDays(weekStart, 7);
    return events.filter(ev => ev.start >= weekStart && ev.start < end).length;
  }, [events, weekStart]);

  const weekHours = useMemo(() => {
    const end = addDays(weekStart, 7);
    return events.filter(ev => ev.start >= weekStart && ev.start < end).reduce((s, ev) => s + (ev.end - ev.start) / 3600000, 0).toFixed(1);
  }, [events, weekStart]);

  /* ─── CRUD ─── */
  const openCreateModal = useCallback(() => {
    const now = new Date();
    setEditingId(null);
    setNewEvent({ title: '', start: setHM(now, now.getHours() + 1, 0), end: setHM(now, now.getHours() + 2, 0), color: COLORS[Math.floor(Math.random() * COLORS.length)], type: 'Online', location: '' });
    setShowModal(true);
  }, []);

  const saveEvent = () => {
    if (!newEvent?.title?.trim()) return;
    if (editingId !== null) {
      setEvents(prev => prev.map(ev => ev.id === editingId ? { ...ev, ...newEvent, id: editingId } : ev));
    } else {
      // Create new event
      setEvents(prev => [...prev, { ...newEvent, id: nextId.current++ }]);
    }
    setShowModal(false); setNewEvent(null); setEditingId(null);
  };
  const deleteEvent = (id) => { setEvents(prev => prev.filter(e => e.id !== id)); setSelectedEvent(null); };

  /* ═══════════════════════════════════════════
     DRAG ENGINE (desktop only — ≥992px)
     ═══════════════════════════════════════════ */

  /** Get the day column index from a mouse X position by checking cached column rects */
  const getDayIndexFromX = useCallback((clientX) => {
    const rects = columnRectsRef.current;
    for (let i = 0; i < rects.length; i++) {
      if (clientX >= rects[i].left && clientX < rects[i].right) return i;
    }
    // Clamp to nearest
    if (rects.length && clientX < rects[0].left) return 0;
    return rects.length - 1;
  }, []);

  /** Get snapped y offset relative to grid top */
  const getSnappedY = useCallback((clientY, gridTop) => {
    const raw = clientY - gridTop;
    const clamped = Math.max(0, Math.min(TOTAL_HOURS * HOUR_H - SNAP_PX, raw));
    return Math.round(clamped / SNAP_PX) * SNAP_PX;
  }, []);

  /** Get visible day columns for the current view */
  const getVisibleDays = useCallback(() => {
    return view === 'day' ? [weekDays[dayIndex]] : weekDays;
  }, [view, weekDays, dayIndex]);

  /* ─── mousedown on event: move or resize (desktop only) ─── */
  const handleEventMouseDown = useCallback((ev, e, isResize) => {
    if (!canDrag) return;
    e.preventDefault(); e.stopPropagation();

    const colEl = e.target.closest('[data-daycolumn]');
    if (!colEl) return;
    const gridEl = colEl.closest('[data-gridcontainer]');
    if (!gridEl) return;

    // Cache all column rects
    const cols = gridEl.querySelectorAll('[data-daycolumn]');
    columnRectsRef.current = Array.from(cols).map(c => c.getBoundingClientRect());
    const gridRect = gridEl.getBoundingClientRect();
    const duration = ev.end - ev.start;

    dragRef.current = {
      mode: isResize ? 'resize' : 'move', eventId: ev.id, event: ev,
      startX: e.clientX, startY: e.clientY,
      gridTop: gridRect.top, gridTopClient: gridRect.top, scrollTop: gridEl.scrollTop, gridEl, duration,
      activated: false,
      originalTop: ((ev.start.getHours() - START_HOUR) + ev.start.getMinutes() / 60) * HOUR_H,
      originalHeight: Math.max((duration / 3600000) * HOUR_H, SNAP_PX),
      // For move: offset from event top to mouse click
      offsetY: e.clientY - (gridRect.top - gridEl.scrollTop + ((ev.start.getHours() - START_HOUR) + ev.start.getMinutes() / 60) * HOUR_H),
    };

    const onMove = (me) => {
      const drag = dragRef.current;
      if (!drag) return;
      const dx = me.clientX - drag.startX;
      const dy = me.clientY - drag.startY;

      // Check threshold
      if (!drag.activated) {
        if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
        drag.activated = true; setIsDragging(true);
      }
      const gridRect2 = drag.gridEl.getBoundingClientRect();
      const scrollTop = drag.gridEl.scrollTop;

      if (drag.mode === 'move') {
        const rawY = me.clientY - gridRect2.top + scrollTop - drag.offsetY;
        const snappedTop = Math.round(Math.max(0, Math.min(TOTAL_HOURS * HOUR_H - drag.originalHeight, rawY)) / SNAP_PX) * SNAP_PX;
        const colIdx = getDayIndexFromX(me.clientX);
        const days = getVisibleDays();
        const targetDate = days[Math.max(0, Math.min(days.length - 1, colIdx))];
        const { h, m } = snapY(snappedTop);
        updateGhost({ mode: 'move', eventId: drag.eventId, top: snappedTop, height: drag.originalHeight, colIdx: Math.max(0, Math.min(days.length - 1, colIdx)), color: drag.event.color, title: drag.event.title, startH: h, startM: m, endH: null, endM: null, targetDate });
      } else {
        // Resize
        const evTopPx = drag.originalTop;
        const rawBottom = me.clientY - gridRect2.top + scrollTop;
        const snappedBottom = Math.round(Math.max(evTopPx + SNAP_PX, Math.min(TOTAL_HOURS * HOUR_H, rawBottom)) / SNAP_PX) * SNAP_PX;
        const newHeight = snappedBottom - evTopPx;
        const { h: endH, m: endM } = snapY(snappedBottom);
        updateGhost({ mode: 'resize', eventId: drag.eventId, top: evTopPx, height: newHeight, colIdx: -1, color: drag.event.color, title: drag.event.title, startH: drag.event.start.getHours(), startM: drag.event.start.getMinutes(), endH, endM, targetDate: null });
      }
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const drag = dragRef.current; dragRef.current = null; setIsDragging(false);
      if (!drag || !drag.activated) { updateGhost(null); if (drag && !isResize) setSelectedEvent(ev); return; }
      const ghost = ghostRef.current; updateGhost(null);
      if (!ghost) return;
      setEvents(evts => evts.map(evt => {
        if (evt.id !== drag.eventId) return evt;
        if (ghost.mode === 'move') {
          const newStart = setHM(ghost.targetDate, ghost.startH, ghost.startM);
          return { ...evt, start: newStart, end: new Date(newStart.getTime() + drag.duration) };
        } else {
          // Resize
          const newEnd = setHM(evt.start, ghost.endH, ghost.endM);
          return newEnd > evt.start ? { ...evt, end: newEnd } : evt;
        }
      }));
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [canDrag, getDayIndexFromX, getVisibleDays, updateGhost]);

  /* ─── mousedown on empty grid: drag-to-create (desktop only) ─── */
  const handleGridMouseDown = useCallback((date, colIdx, e) => {
    if (!canDrag) return;
    if (e.target.closest && e.target.closest('[data-event-block]')) return;
    e.preventDefault();
    const gridEl = e.currentTarget.closest('[data-gridcontainer]');
    if (!gridEl) return;
    const cols = gridEl.querySelectorAll('[data-daycolumn]');
    columnRectsRef.current = Array.from(cols).map(c => c.getBoundingClientRect());
    const gridRect = gridEl.getBoundingClientRect();
    const scrollTop = gridEl.scrollTop;
    const rawY = e.clientY - gridRect.top + scrollTop;
    const snappedY2 = Math.round(Math.max(0, rawY) / SNAP_PX) * SNAP_PX;

    dragRef.current = { mode: 'create', startX: e.clientX, startY: e.clientY, anchorY: snappedY2, gridEl, colIdx, date, activated: false };

    const onMove = (me) => {
      const drag = dragRef.current; if (!drag) return;
      const dx = me.clientX - drag.startX;
      const dy = me.clientY - drag.startY;
      if (!drag.activated) { if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return; drag.activated = true; setIsDragging(true); }
      const gRect = drag.gridEl.getBoundingClientRect();
      const sTop = drag.gridEl.scrollTop;
      const rawY2 = me.clientY - gRect.top + sTop;
      const snapped = Math.round(Math.max(0, Math.min(TOTAL_HOURS * HOUR_H, rawY2)) / SNAP_PX) * SNAP_PX;
      const topPx = Math.min(drag.anchorY, snapped);
      const bottomPx = Math.max(drag.anchorY, snapped);
      const height = Math.max(bottomPx - topPx, SNAP_PX);
      const { h: sH, m: sM } = snapY(topPx);
      const { h: eH, m: eM } = snapY(topPx + height);
      updateGhost({ mode: 'create', eventId: null, top: topPx, height, colIdx: drag.colIdx, color: C.primary, title: 'New Event', startH: sH, startM: sM, endH: eH, endM: eM, targetDate: drag.date });
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const drag = dragRef.current; dragRef.current = null; setIsDragging(false);
      if (!drag || !drag.activated) {
        updateGhost(null);
        // It was a click — open create modal at clicked time
        if (drag) {
          const { h, m } = snapY(drag.anchorY);
          setNewEvent({ title: '', start: setHM(drag.date, h, m), end: setHM(drag.date, h + 1, m), color: COLORS[Math.floor(Math.random() * COLORS.length)], type: 'Online', location: '' });
          setShowModal(true);
        }
        return;
      }
      const ghost = ghostRef.current; updateGhost(null);
      if (!ghost) return;
      const start = setHM(drag.date, ghost.startH, ghost.startM);
      const end = setHM(drag.date, ghost.endH, ghost.endM);
      if (end <= start) return;
      setNewEvent({ title: '', start, end, color: COLORS[Math.floor(Math.random() * COLORS.length)], type: 'Online', location: '' });
      setShowModal(true);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [canDrag, updateGhost]);

  /* ─── Tap on empty grid space (mobile/tablet) ─── */
  const handleGridTap = useCallback((date, e) => {
    if (e.target.closest && e.target.closest('[data-event-block]')) return;
    const gridEl = e.currentTarget.closest('[data-gridcontainer]');
    if (!gridEl) return;
    const gridRect = gridEl.getBoundingClientRect();
    const scrollTop = gridEl.scrollTop;
    const rawY = e.clientY - gridRect.top + scrollTop;
    const snappedPx = Math.round(Math.max(0, rawY) / SNAP_PX) * SNAP_PX;
    const { h, m } = snapY(snappedPx);
    setEditingId(null);
    setNewEvent({
      title: '', start: setHM(date, h, m), end: setHM(date, h + 1, m),
      color: COLORS[Math.floor(Math.random() * COLORS.length)], type: 'Online', location: '',
    });
    setShowModal(true);
  }, []);

  /* ═══════════════════════════════════════════
     Render helpers
     ═══════════════════════════════════════════ */
  const renderTimeColumn = () => (
    <div style={{ width: timeColW, flexShrink: 0, userSelect: 'none' }}>
      {Array.from({ length: TOTAL_HOURS }, (_, i) => (
        <div key={i} style={{ height: HOUR_H, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', paddingRight: mob ? 6 : 10 }}>
          <span style={{ fontSize: mob ? 9 : 10, fontWeight: 600, color: C.t3, transform: 'translateY(-6px)' }}>
            {fmtTime(START_HOUR + i, 0)}
          </span>
        </div>
      ))}
    </div>
  );

  const renderDayColumn = (date, colIdx, colCount) => {
    const dayEvents = eventsForDay(date);
    const isToday = sameDay(date, today);

    // Lane assignment for overlaps
    const sorted = [...dayEvents].sort((a, b) => a.start - b.start);
    const lanes = [];
    sorted.forEach((ev) => {
      let placed = false;
      for (let l = 0; l < lanes.length; l++) {
        if (lanes[l].every(x => ev.start >= x.end || ev.end <= x.start)) {
          lanes[l].push(ev); ev._lane = l; placed = true; break;
        }
      }
      if (!placed) { ev._lane = lanes.length; lanes.push([ev]); }
    });
    const maxLanes = Math.max(lanes.length, 1);

    return (
      <div
        key={date.toISOString()}
        data-daycolumn={colIdx}
        style={{
          flex: 1, position: 'relative',
          borderLeft: `1px solid ${C.bdr}`,
          background: isToday ? 'rgba(236,91,19,.015)' : 'transparent',
          cursor: canDrag ? (isDragging ? (dragRef.current?.mode === 'resize' ? 'ns-resize' : 'grabbing') : 'cell') : 'default',
          minWidth: colCount === 1 ? undefined : 0,
          userSelect: 'none',
        }}
        onMouseDown={canDrag ? (e) => handleGridMouseDown(date, colIdx, e) : undefined}
        onClick={!canDrag ? (e) => handleGridTap(date, e) : undefined}
      >
        {/* Hour + half-hour gridlines */}
        {Array.from({ length: TOTAL_HOURS }, (_, i) => (
          <div key={i} style={{ height: HOUR_H, borderBottom: '1px solid #f1eeec', position: 'relative', pointerEvents: 'none' }}>
            <div style={{ position: 'absolute', top: HOUR_H / 2, left: 0, right: 0, borderBottom: '1px dashed #f5f2f0' }} />
          </div>
        ))}

        {/* Event blocks */}
        {sorted.map((ev) => {
          const top = ((ev.start.getHours() - START_HOUR) + ev.start.getMinutes() / 60) * HOUR_H;
          const height = Math.max(((ev.end - ev.start) / 3600000) * HOUR_H, 24);
          const laneW = maxLanes > 1 ? `calc(${100 / maxLanes}% - 4px)` : 'calc(100% - 8px)';
          const laneLeft = maxLanes > 1 ? `calc(${(ev._lane / maxLanes) * 100}% + 2px)` : '4px';
          const isBeingDragged = dragGhost && dragGhost.eventId === ev.id;

          return (
            <div
              key={ev.id}
              data-event-block
              onMouseDown={canDrag ? (e) => handleEventMouseDown(ev, e, false) : undefined}
              onClick={!canDrag ? () => setSelectedEvent(ev) : undefined}
              style={{
                position: 'absolute', top, left: laneLeft, width: laneW, height,
                background: `${ev.color}${isBeingDragged ? '08' : '12'}`,
                borderLeft: `3px solid ${ev.color}`,
                borderRadius: 6, padding: mob ? '3px 6px' : '4px 8px',
                cursor: canDrag ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
                overflow: 'hidden', fontSize: mob ? 10 : 11, zIndex: isBeingDragged ? 2 : 5,
                opacity: isBeingDragged ? 0.35 : 1,
                transition: isBeingDragged ? 'none' : 'box-shadow .15s',
                boxShadow: '0 1px 3px rgba(0,0,0,.04)',
                userSelect: 'none',
              }}
              onMouseEnter={canDrag ? (e) => { if (!isDragging) e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,.1)'; } : undefined}
              onMouseLeave={canDrag ? (e) => { if (!isDragging) e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,.04)'; } : undefined}
            >
              <div style={{ fontWeight: 700, color: ev.color, lineHeight: 1.2, marginBottom: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {ev.title}
              </div>
              {height > 36 && (
                <div style={{ fontSize: mob ? 9 : 10, color: C.t2, whiteSpace: 'nowrap' }}>
                  {fmtTimeFromDate(ev.start)} – {fmtTimeFromDate(ev.end)}
                </div>
              )}
              {height > 56 && !mob && (
                <div style={{ fontSize: 10, color: C.t3, marginTop: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Icon name={ev.type === 'On-site' ? 'location_on' : ev.type === 'Internal' ? 'group' : 'videocam'} size={11} style={{ color: C.t3 }} />
                  {ev.location}
                </div>
              )}

              {/* Resize handle (desktop only) */}
              {canDrag && (
                <div data-event-block
                  onMouseDown={(e) => { e.stopPropagation(); handleEventMouseDown(ev, e, true); }}
                  style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 8, cursor: 'ns-resize', display: 'flex', justifyContent: 'center', alignItems: 'center', borderBottomLeftRadius: 6, borderBottomRightRadius: 6 }}>
                  <div style={{ width: 24, height: 3, borderRadius: 2, background: `${ev.color}40` }} />
                </div>
              )}
            </div>
          );
        })}

        {/* Drag ghost (desktop only) */}
        {dragGhost && dragGhost.colIdx === colIdx && (
          <div data-drag-ghost style={{ position: 'absolute', top: dragGhost.top, left: 4, right: 4, height: dragGhost.height, background: `${dragGhost.color}18`, borderLeft: `3px solid ${dragGhost.color}`, borderRadius: 6, padding: '4px 8px', zIndex: 20, pointerEvents: 'none', boxShadow: '0 8px 24px rgba(0,0,0,.12)', border: `1px dashed ${dragGhost.color}60`, borderLeftWidth: 3, borderLeftStyle: 'solid', userSelect: 'none' }}>
            <div style={{ fontWeight: 700, color: dragGhost.color, fontSize: 11, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {dragGhost.title}
            </div>
            <div style={{ fontSize: 10, color: C.t2, marginTop: 2 }}>
              {fmtTime(dragGhost.startH, dragGhost.startM)} – {fmtTime(dragGhost.endH ?? dragGhost.startH, dragGhost.endM ?? dragGhost.startM)}
            </div>
          </div>
        )}

        {/* Now line */}
        {isToday && nowTop !== null && (
          <div style={{ position: 'absolute', top: nowTop, left: 0, right: 0, zIndex: 10, pointerEvents: 'none' }}>
            <div style={{ height: 2, background: C.primary, position: 'relative' }}>
              <div style={{ position: 'absolute', left: -5, top: -4, width: 10, height: 10, borderRadius: '50%', background: C.primary }} />
            </div>
          </div>
        )}
      </div>
    );
  };

  /* ───────── Mini month ───────── */
  const renderMiniMonth = () => {
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    const startDow = first.getDay() === 0 ? 6 : first.getDay() - 1;
    const dim = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= dim; d++) cells.push(d);
    while (cells.length % 7) cells.push(null);

    return (
      <Card style={{ padding: mob ? 14 : 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 700 }}>{MONTH_NAMES[today.getMonth()]} {today.getFullYear()}</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', textAlign: 'center' }}>
          {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
            <div key={i} style={{ fontSize: 10, fontWeight: 700, color: C.t3, padding: '4px 0' }}>{d}</div>
          ))}
          {cells.map((d, i) => {
            const isNow = d === today.getDate();
            const hasEvent = d ? events.some(ev => ev.start.getDate() === d && ev.start.getMonth() === today.getMonth()) : false;
            return (
              <div key={i} style={{ padding: '5px 0', fontSize: 11, fontWeight: isNow ? 800 : 500, color: d ? (isNow ? '#fff' : C.t1) : 'transparent', background: isNow ? C.primary : 'transparent', borderRadius: isNow ? '50%' : 0, width: 28, height: 28, display: 'grid', placeItems: 'center', margin: '1px auto', position: 'relative', cursor: d ? 'pointer' : 'default' }}>
                {d || ''}
                {hasEvent && !isNow && <div style={{ position: 'absolute', bottom: 1, left: '50%', transform: 'translateX(-50%)', width: 4, height: 4, borderRadius: '50%', background: C.primary }} />}
              </div>
            );
          })}
        </div>
      </Card>
    );
  };

  /* ───────── Month view ───────── */
  const renderMonthView = () => {
    const refDate = weekStart;
    const monthStart = new Date(refDate.getFullYear(), refDate.getMonth(), 1);
    const startDow = monthStart.getDay() === 0 ? 6 : monthStart.getDay() - 1;
    const dim = new Date(refDate.getFullYear(), refDate.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push({ day: null });
    for (let d = 1; d <= dim; d++) {
      const date = new Date(refDate.getFullYear(), refDate.getMonth(), d);
      cells.push({ day: d, date, events: eventsForDay(date) });
    }
    while (cells.length % 7) cells.push({ day: null });

    return (
      <Card style={{ overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', borderBottom: `1px solid ${C.bdr}` }}>
          {(mob ? ['M', 'T', 'W', 'T', 'F', 'S', 'S'] : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']).map((d, i) => (
            <div key={i} style={{ padding: mob ? '8px 4px' : '10px 12px', fontSize: mob ? 10 : 11, fontWeight: 700, color: C.t3, textTransform: 'uppercase', textAlign: 'center', borderLeft: i > 0 ? `1px solid ${C.bdr}` : 'none' }}>{d}</div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)' }}>
          {cells.map((cell, i) => {
            const isT = cell.date && sameDay(cell.date, today);
            return (
              <div key={i} style={{
                minHeight: mob ? 52 : 100, borderLeft: `1px solid ${C.bdr}`, borderBottom: `1px solid ${C.bdr}`,
                padding: mob ? 4 : 6, background: cell.day ? (isT ? 'rgba(236,91,19,.03)' : C.w) : '#fafafa',
                cursor: cell.day ? 'pointer' : 'default',
              }}
                onClick={() => { if (cell.date) { setWeekStart(getMonday(cell.date)); setDayIndex(cell.date.getDay() === 0 ? 6 : cell.date.getDay() - 1); setView('day'); } }}>
                {cell.day && (
                  <>
                    <div style={{ fontSize: mob ? 11 : 12, fontWeight: isT ? 800 : 500, color: isT ? C.primary : C.t1, marginBottom: 2, width: mob ? 20 : 24, height: mob ? 20 : 24, borderRadius: '50%', background: isT ? C.pL : 'transparent', display: 'grid', placeItems: 'center' }}>{cell.day}</div>
                    {!mob && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {cell.events.slice(0, 3).map(ev => (
                          <div key={ev.id} style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: `${ev.color}14`, color: ev.color, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{ev.title}</div>
                        ))}
                        {cell.events.length > 3 && <div style={{ fontSize: 10, color: C.t3, fontWeight: 600, paddingLeft: 4 }}>+{cell.events.length - 3} more</div>}
                      </div>
                    )}
                    {mob && cell.events.length > 0 && (
                      <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginTop: 2 }}>
                        {cell.events.slice(0, 3).map(ev => (
                          <div key={ev.id} style={{ width: 6, height: 6, borderRadius: '50%', background: ev.color }} />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    );
  };

  /* ─── Form input helpers ─── */
  const fmtDateInput = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const labelSt = { display: 'block', fontSize: 12, fontWeight: 600, color: C.t2, marginBottom: 6 };
  const inputSt = { width: '100%', padding: '10px 14px', borderRadius: 10, border: `1px solid ${C.bdr}`, fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
  const focusH = {
    onFocus: e => { e.target.style.borderColor = C.primary; e.target.style.boxShadow = `0 0 0 3px ${C.pL}`; },
    onBlur: e => { e.target.style.borderColor = C.bdr; e.target.style.boxShadow = 'none'; },
  };

  /* Show sidebar? Only on week/day view and wide screens */
  const showSidebar = view !== 'month' && isWide;

  /* ═══════════════════════════════════════════
     MAIN RENDER
     ═══════════════════════════════════════════ */
  return (
    <div style={{ height: '100%' }}>
    <div style={{ height: '100%', overflow: 'auto' }}>
    <div style={{ padding: `${mob ? 16 : 32}px ${pad}px ${mob ? 80 : 48}px`, userSelect: isDragging ? 'none' : 'auto' }}>
      {!mob && <Breadcrumb items={['Dashboard', 'Scheduling']} />}

      {/* ═══ Toolbar ═══ */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: mob ? 12 : 16, flexWrap: 'wrap', gap: mob ? 8 : 12 }}>
        {/* Left: nav */}
        <div style={{ display: 'flex', alignItems: 'center', gap: mob ? 6 : 12, minWidth: 0, flex: 1 }}>
          <button onClick={goToday} style={{ padding: mob ? '5px 10px' : '6px 14px', borderRadius: 8, border: `1px solid ${C.bdr}`, background: C.w, fontSize: mob ? 11 : 12, fontWeight: 700, cursor: 'pointer', color: C.t1, flexShrink: 0 }}>Today</button>
          <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
            <button onClick={goPrev} style={{ width: mob ? 28 : 32, height: mob ? 28 : 32, borderRadius: 8, border: `1px solid ${C.bdr}`, background: C.w, cursor: 'pointer', display: 'grid', placeItems: 'center', color: C.t2 }}><Icon name="chevron_left" size={mob ? 18 : 20} /></button>
            <button onClick={goNext} style={{ width: mob ? 28 : 32, height: mob ? 28 : 32, borderRadius: 8, border: `1px solid ${C.bdr}`, background: C.w, cursor: 'pointer', display: 'grid', placeItems: 'center', color: C.t2 }}><Icon name="chevron_right" size={mob ? 18 : 20} /></button>
          </div>
          <h2 style={{ fontSize: mob ? 16 : 22, fontWeight: 800, letterSpacing: '-.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{titleStr}</h2>
        </div>

        {/* Right: view toggle + create */}
        <div style={{ display: 'flex', gap: mob ? 6 : 10, alignItems: 'center', flexShrink: 0 }}>
          <div style={{ display: 'flex', background: '#f1eeec', borderRadius: 10, padding: 3 }}>
            {['day', 'week', 'month'].map(v => (
              <button key={v} onClick={() => setView(v)} style={{
                padding: mob ? '5px 10px' : '6px 16px', borderRadius: 8, border: 'none',
                background: view === v ? C.w : 'transparent', fontWeight: view === v ? 700 : 500,
                fontSize: mob ? 11 : 12, cursor: 'pointer', color: view === v ? C.primary : C.t2,
                boxShadow: view === v ? '0 1px 3px rgba(0,0,0,.06)' : 'none', textTransform: 'capitalize',
              }}>{v}</button>
            ))}
          </div>
          {/* Desktop/tablet: inline create button. Mobile: FAB below */}
          {!mob && (
            <Btn primary icon="add" onClick={openCreateModal}>
              {isTablet ? 'Create' : 'Create Meeting'}
            </Btn>
          )}
        </div>
      </div>

      {/* ═══ Filters ═══ */}
      <div style={{ display: 'flex', gap: mob ? 6 : 8, marginBottom: mob ? 14 : 20, flexWrap: 'wrap' }}>
        {['All', 'Online', 'On-site', 'Internal'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: mob ? '5px 12px' : '6px 16px', borderRadius: 99,
            fontSize: mob ? 11 : 12, fontWeight: filter === f ? 700 : 500,
            border: filter === f ? 'none' : `1px solid ${C.bdr}`,
            background: filter === f ? C.primary : C.w, color: filter === f ? '#fff' : C.t2,
            cursor: 'pointer',
          }}>
            {f === 'All' ? (mob ? 'All' : 'All Schedules') : f}
          </button>
        ))}
      </div>

      {/* ═══ Calendar + sidebar ═══ */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: showSidebar ? '1fr 280px' : '1fr',
        gap: mob ? 16 : 24,
      }}>
        {view === 'month' ? renderMonthView() : (
          <Card style={{ overflow: 'hidden' }}>
            {/* Day header row — WEEK */}
            {view === 'week' && (
              <div style={{ display: 'flex', borderBottom: `1px solid ${C.bdr}`, background: C.bg }}>
                <div style={{ width: timeColW, flexShrink: 0 }} />
                {weekDays.map((d, i) => {
                  const isT = sameDay(d, today);
                  return (
                    <div key={i} style={{
                      flex: 1, padding: mob ? '6px 0' : '10px 0', textAlign: 'center',
                      borderLeft: `1px solid ${C.bdr}`, background: isT ? C.pL : 'transparent', cursor: 'pointer',
                      minWidth: 0,
                    }}
                      onClick={() => { setDayIndex(i); setView('day'); }}>
                      <div style={{ fontSize: mob ? 9 : 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: isT ? C.primary : C.t3 }}>
                        {mob ? DAY_NAMES[d.getDay()].charAt(0) : DAY_NAMES[d.getDay()]}
                      </div>
                      <div style={{ fontSize: mob ? 14 : 20, fontWeight: isT ? 900 : 700, color: isT ? C.primary : C.t1, lineHeight: 1.2, marginTop: 2 }}>{d.getDate()}</div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Day header row — DAY */}
            {view === 'day' && (
              <div style={{ display: 'flex', borderBottom: `1px solid ${C.bdr}`, background: C.bg }}>
                <div style={{ width: timeColW, flexShrink: 0 }} />
                <div style={{ flex: 1, padding: mob ? '10px 12px' : '12px 16px', borderLeft: `1px solid ${C.bdr}`, background: sameDay(weekDays[dayIndex], today) ? C.pL : 'transparent' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: C.t3, marginBottom: 2 }}>{DAY_NAMES[weekDays[dayIndex].getDay()]}</div>
                  <div style={{ fontSize: mob ? 20 : 24, fontWeight: 900, color: sameDay(weekDays[dayIndex], today) ? C.primary : C.t1 }}>{weekDays[dayIndex].getDate()}</div>
                </div>
              </div>
            )}

            {/* Time grid body */}
            <div data-gridcontainer style={{ height: TOTAL_HOURS * HOUR_H, overflowY: 'auto', display: 'flex', position: 'relative' }}>
              {nowTop !== null && (
                <div style={{ position: 'absolute', top: nowTop - 6, left: 0, width: timeColW, textAlign: 'right', paddingRight: mob ? 4 : 8, fontSize: mob ? 9 : 10, fontWeight: 700, color: C.primary, zIndex: 15, pointerEvents: 'none' }}>
                  {fmtTimeFromDate(currentTime)}
                </div>
              )}
              {renderTimeColumn()}
              {view === 'week'
                ? weekDays.map((d, i) => renderDayColumn(d, i, 7))
                : renderDayColumn(weekDays[dayIndex], 0, 1)
              }
            </div>
          </Card>
        )}

        {/* ═══ Right sidebar ═══ */}
        {showSidebar && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Mini month */}
            {renderMiniMonth()}

            {/* Upcoming today */}
            <Card style={{ padding: 18 }}>
              <h4 style={{ fontSize: 10, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 14 }}>Upcoming Today</h4>
              {upcomingToday.length === 0 && <p style={{ fontSize: 12, color: C.t3, fontStyle: 'italic' }}>No more events today</p>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {upcomingToday.map(u => (
                  <div key={u.id} style={{ display: 'flex', gap: 10, padding: '8px 10px', borderRadius: 8, border: `1px solid ${C.bdr}`, background: `${u.color}08`, cursor: 'pointer' }} onClick={() => setSelectedEvent(u)}>
                    <div style={{ width: 3, borderRadius: 4, background: u.color, flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 2, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{u.title}</div>
                      <div style={{ fontSize: 11, color: C.t2 }}>{fmtTimeFromDate(u.start)} – {fmtTimeFromDate(u.end)}</div>
                      <Pill bg={`${u.color}14`} color={u.color}>{u.type}</Pill>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
            <Card style={{ padding: 18 }}>
              <h4 style={{ fontSize: 10, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 12 }}>Quick Stats</h4>
              {[['Meetings this week', weekEventCount], ['Hours scheduled', `${weekHours}h`], ['Events today', eventsForDay(today).length]].map(([l, v]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: `1px solid ${C.bdr}`, fontSize: 13 }}>
                  <span style={{ color: C.t2 }}>{l}</span><span style={{ fontWeight: 700 }}>{v}</span>
                </div>
              ))}
            </Card>
          </div>
        )}

        {/* ═══ Inline stats (non-wide, non-month, non-mobile) ═══ */}
        {!showSidebar && !mob && view !== 'month' && (
          <div style={{ gridColumn: '1/-1', display: 'flex', gap: 16 }}>
            <div style={{ flex: 1 }}>{renderMiniMonth()}</div>
            <Card style={{ padding: 18, flex: 1 }}>
              <h4 style={{ fontSize: 10, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 14 }}>Upcoming Today</h4>
              {upcomingToday.length === 0 && <p style={{ fontSize: 12, color: C.t3, fontStyle: 'italic' }}>No more events today</p>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {upcomingToday.map(u => (
                  <div key={u.id} style={{ display: 'flex', gap: 10, padding: '8px 10px', borderRadius: 8, border: `1px solid ${C.bdr}`, background: `${u.color}08`, cursor: 'pointer' }} onClick={() => setSelectedEvent(u)}>
                    <div style={{ width: 3, borderRadius: 4, background: u.color, flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 2, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{u.title}</div>
                      <div style={{ fontSize: 11, color: C.t2 }}>{fmtTimeFromDate(u.start)} – {fmtTimeFromDate(u.end)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
            <Card style={{ padding: 18, flex: 1 }}>
              <h4 style={{ fontSize: 10, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 12 }}>Quick Stats</h4>
              {[['Meetings this week', weekEventCount], ['Hours scheduled', `${weekHours}h`], ['Events today', eventsForDay(today).length]].map(([l, v]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: `1px solid ${C.bdr}`, fontSize: 13 }}>
                  <span style={{ color: C.t2 }}>{l}</span><span style={{ fontWeight: 700 }}>{v}</span>
                </div>
              ))}
            </Card>
          </div>
        )}
      </div>

    </div>
    </div>
    {/* ═══════ Modals + FAB — rendered OUTSIDE scroll container ═══════ */}

      {/* ═══ Event detail popover ═══ */}
      {selectedEvent && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'grid', placeItems: 'center', padding: mob ? 16 : 24 }} onClick={() => setSelectedEvent(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            width: '100%', maxWidth: mob ? '92%' : 360,
            background: C.w, borderRadius: 14, border: `1px solid ${C.bdr}`,
            boxShadow: '0 20px 60px rgba(0,0,0,.12)', overflow: 'hidden',
            maxHeight: '90vh', display: 'flex', flexDirection: 'column',
          }}>
            <div style={{ height: 6, background: selectedEvent.color, flexShrink: 0 }} />
            <div style={{ padding: mob ? 20 : 24, overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <h3 style={{ fontSize: mob ? 16 : 18, fontWeight: 700, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedEvent.title}</h3>
                <button onClick={() => setSelectedEvent(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: C.t3, display: 'grid', placeItems: 'center', marginLeft: 8, flexShrink: 0 }}><Icon name="close" size={20} /></button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Icon name="schedule" size={18} style={{ color: C.t3, flexShrink: 0 }} />
                  <span style={{ color: C.t2 }}>{fmtTimeFromDate(selectedEvent.start)} – {fmtTimeFromDate(selectedEvent.end)}<span style={{ marginLeft: 8, color: C.t3 }}>({((selectedEvent.end - selectedEvent.start) / 3600000).toFixed(1)}h)</span></span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Icon name="calendar_today" size={18} style={{ color: C.t3, flexShrink: 0 }} />
                  <span style={{ color: C.t2 }}>{DAY_NAMES[selectedEvent.start.getDay()]}, {selectedEvent.start.getDate()} {MONTH_NAMES[selectedEvent.start.getMonth()]}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <Icon name={selectedEvent.type === 'On-site' ? 'location_on' : 'videocam'} size={18} style={{ color: C.t3, flexShrink: 0 }} />
                  <span style={{ color: C.t2 }}>{selectedEvent.location}</span>
                  <Pill bg={`${selectedEvent.color}14`} color={selectedEvent.color}>{selectedEvent.type}</Pill>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 24, paddingTop: 16, borderTop: `1px solid ${C.bdr}` }}>
                <Btn icon="edit" style={{ flex: 1, justifyContent: 'center' }} onClick={() => {
                  setEditingId(selectedEvent.id);
                  setNewEvent({ title: selectedEvent.title, start: new Date(selectedEvent.start), end: new Date(selectedEvent.end), color: selectedEvent.color, type: selectedEvent.type, location: selectedEvent.location });
                  setSelectedEvent(null); setShowModal(true);
                }}>Edit</Btn>
                <button onClick={() => deleteEvent(selectedEvent.id)} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px 18px', borderRadius: 12, border: `1px solid ${C.red}30`, background: `${C.red}08`, color: C.red, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  <Icon name="delete" size={16} />Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Create / Edit event modal ═══ */}
      {showModal && newEvent && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,.25)', display: 'grid', placeItems: 'center', padding: mob ? 12 : 24 }}
          onClick={() => { setShowModal(false); setEditingId(null); }}>
          <div onClick={e => e.stopPropagation()} style={{
            width: '100%', maxWidth: mob ? undefined : 420,
            ...(mob ? { width: '94%' } : {}),
            maxHeight: mob ? '95vh' : '90vh',
            background: C.w, borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,.15)',
            overflow: 'hidden', display: 'flex', flexDirection: 'column',
          }}>
            <div style={{ padding: mob ? '16px 20px' : '20px 24px', borderBottom: `1px solid ${C.bdr}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <h3 style={{ fontSize: mob ? 16 : 18, fontWeight: 700 }}>{editingId !== null ? 'Edit Meeting' : 'New Meeting'}</h3>
              <button onClick={() => { setShowModal(false); setEditingId(null); }} style={{ border: 'none', background: 'none', cursor: 'pointer', color: C.t3, display: 'grid', placeItems: 'center' }}><Icon name="close" size={20} /></button>
            </div>
            <div style={{ padding: mob ? '16px 20px' : 24, display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto', flex: 1 }}>
              {/* Title */}
              <div>
                <label style={labelSt}>Title</label>
                <input value={newEvent.title} onChange={e => setNewEvent({ ...newEvent, title: e.target.value })} placeholder="Meeting title…" autoFocus style={inputSt} {...focusH} />
              </div>
              {/* Date + Time row */}
              <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelSt}>Date</label>
                  <input type="date" value={fmtDateInput(newEvent.start)}
                    onChange={e => {
                      const [y, mo, d] = e.target.value.split('-').map(Number);
                      setNewEvent(p => ({
                        ...p,
                        start: setHM(new Date(y, mo - 1, d), p.start.getHours(), p.start.getMinutes()),
                        end: setHM(new Date(y, mo - 1, d), p.end.getHours(), p.end.getMinutes()),
                      }));
                    }}
                    style={inputSt} />
                </div>
                <div>
                  <label style={labelSt}>Start</label>
                  <input type="time" value={fmtTimeFromDate(newEvent.start)} onChange={e => { const [h, m] = e.target.value.split(':').map(Number); setNewEvent(p => ({ ...p, start: setHM(p.start, h, m) })); }} style={inputSt} />
                </div>
                <div>
                  <label style={labelSt}>End</label>
                  <input type="time" value={fmtTimeFromDate(newEvent.end)} onChange={e => { const [h, m] = e.target.value.split(':').map(Number); setNewEvent(p => ({ ...p, end: setHM(p.end, h, m) })); }} style={inputSt} />
                </div>
              </div>
              {/* Type */}
              <div>
                <label style={labelSt}>Type</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['Online', 'On-site', 'Internal'].map(t => (
                    <button key={t} onClick={() => setNewEvent({ ...newEvent, type: t })} style={{ flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: newEvent.type === t ? `2px solid ${C.primary}` : `1px solid ${C.bdr}`, background: newEvent.type === t ? C.pL : C.w, color: newEvent.type === t ? C.primary : C.t2 }}>{t}</button>
                  ))}
                </div>
              </div>
              {/* Location */}
              <div>
                <label style={labelSt}>Location</label>
                <input value={newEvent.location} onChange={e => setNewEvent({ ...newEvent, location: e.target.value })} placeholder="Zoom, HQ Room 3…" style={inputSt} {...focusH} />
              </div>
              {/* Color */}
              <div>
                <label style={labelSt}>Color</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {COLORS.map(c => (
                    <button key={c} onClick={() => setNewEvent({ ...newEvent, color: c })} style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', background: c, cursor: 'pointer', boxShadow: newEvent.color === c ? `0 0 0 3px ${c}44` : 'none', transform: newEvent.color === c ? 'scale(1.15)' : 'scale(1)', transition: 'transform .15s, box-shadow .15s' }} />
                  ))}
                </div>
              </div>
            </div>
            {/* Actions */}
            <div style={{ padding: mob ? '14px 20px' : '16px 24px', borderTop: `1px solid ${C.bdr}`, display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0 }}>
              <Btn onClick={() => { setShowModal(false); setEditingId(null); }}>Cancel</Btn>
              <Btn primary icon="check" onClick={saveEvent}>{editingId !== null ? 'Save' : (mob ? 'Create' : 'Create Meeting')}</Btn>
            </div>
          </div>
        </div>
      )}

    {/* ═══ Mobile FAB — floating "+" button (OUTSIDE scroll container) ═══ */}
    {mob && (
      <button onClick={openCreateModal} aria-label="Create meeting" style={{
        position: 'fixed', bottom: 24, right: 20,
        width: 56, height: 56, borderRadius: 16, border: 'none',
        background: C.primary, color: '#fff', cursor: 'pointer',
        display: 'grid', placeItems: 'center',
        boxShadow: '0 6px 20px rgba(236,91,19,.35)', zIndex: 90,
        WebkitTapHighlightColor: 'transparent',
      }}>
        <Icon name="add" size={28} style={{ color: '#fff' }} />
      </button>
    )}
    </div>
  );
}
